import React, { useState, useEffect } from "react";
import { X, EyeOff } from "lucide-react";

// Reasons mirror Lead.SELLER_IGNORE_REASONS / BUYER_IGNORE_REASONS on the backend
// (#12984261525). Buyer and seller keep their own "not genuine" code so the data
// never mixes (Christian Gravias, Monday 13225447621).
export const IGNORE_REASONS = [
  { value: "spam_fake", label: "Fake / spam lead" },
  { value: "invalid_contact", label: "Invalid contact details" },
  { value: "not_genuine", label: "Not a genuine seller / no real intent" },
  { value: "duplicate", label: "Duplicate / already known" },
  { value: "other", label: "Other" },
];

export const BUYER_IGNORE_REASONS = IGNORE_REASONS.map((r) => (
  r.value === "not_genuine"
    ? { value: "not_genuine_buyer", label: "Not a genuine buyer / no real intent" }
    : r
));

/**
 * "Why are you ignoring this lead?" pop-up. Captures a reason plus a note. The
 * note is only compulsory for "Other", where the reason alone says nothing; the
 * named reasons speak for themselves, so the note there is optional
 * (#12984261525, revised on #13058325657). onConfirm(reason, notes) fires the
 * actual ignore. kind="buyer" (Buyer Enquiries & Offers) swaps in the buyer list.
 */
export default function IgnoreLeadReasonModal({ open, onClose, onConfirm, busy = false, count = 1, kind = "seller" }) {
  const reasons = kind === "buyer" ? BUYER_IGNORE_REASONS : IGNORE_REASONS;
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  // Only "Other" needs an explanation typed out; the rest are self-describing.
  const noteRequired = reason === "other";
  const noteMissing = noteRequired && !notes.trim();
  const canConfirm = Boolean(reason) && !noteMissing;

  useEffect(() => {
    if (open) { setReason(""); setNotes(""); }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <EyeOff className="w-4 h-4 text-gray-500" />
            Why are you ignoring this lead{count > 1 ? "s" : ""}?
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-1.5">
          {reasons.map((r) => (
            <label key={r.value} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 has-[:checked]:border-[#55C5D0] has-[:checked]:bg-[#55C5D0]/5">
              <input
                type="radio"
                name="ignore_reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
                className="text-[#55C5D0] focus:ring-[#55C5D0]"
              />
              <span className="text-sm text-gray-700">{r.label}</span>
            </label>
          ))}

          <div className="pt-2">
            <label className="text-xs font-bold text-gray-600">
              Notes {noteRequired ? <span className="text-[#c8392f]">(required)</span> : <span className="font-normal text-gray-400">(optional)</span>}
            </label>
            <textarea
              rows="2"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={noteRequired
                ? "Please explain why this lead was ignored."
                : "Optional: any extra context on why this lead was poor."}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0]"
            />
            {noteMissing && (
              <p className="mt-1 text-[11px] text-[#c8392f]">A note is required when you pick "Other", so we know what happened.</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-bold hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button
            onClick={() => canConfirm && onConfirm(reason, notes.trim())}
            disabled={!canConfirm || busy}
            className="px-4 py-2 rounded-lg bg-[#c8392f] text-white text-sm font-bold hover:bg-[#b13228] disabled:opacity-50 flex items-center gap-1.5"
          >
            <EyeOff className="w-4 h-4" /> {busy ? "Ignoring…" : "Confirm Ignore"}
          </button>
        </div>
      </div>
    </div>
  );
}
